import { ConfigElements } from "@constants/common";
import KatalLogger, { LoggerConfig } from "@amzn/katal-logger";
import {
    MetricName,
    MetricOutcome,
    ToolbarMetricName,
} from "@constants/logging";
import {
    DimensionSet,
    EmfHttpStatusCodeMetric,
    EmfMetric,
} from "@customTypes/logging";
import {
    createEmfFormattedMetric,
    createEmfHttpStatusCodeMetric,
    getCommonProperties,
} from "./emfUtils";

export class ErmineLogger extends KatalLogger {
    constructor(config?: LoggerConfig) {
        super({ ...config });
    }

    public logMessage(
        message: string,
        context?: Record<string, unknown>
    ): void {
        void this.info(message, {
            ...(context ?? {}),
            ...getCommonProperties(),
        });
    }

    public publishCounterMetric(
        metricName: MetricName | ToolbarMetricName,
        count = 1
    ): void {
        const emfMetric: EmfMetric = {
            name: metricName,
            value: count,
            unit: "Count",
            dimensions: {},
        };
        void this.info(metricName, createEmfFormattedMetric(emfMetric));
    }

    public publishMetricWithDimension(
        metricName: MetricName | ToolbarMetricName,
        dimensionValue: string
    ): void {
        const emfMetric: EmfMetric = {
            name: metricName,
            value: 1,
            unit: "Count",
            dimensions: { dimension: dimensionValue },
        };
        void this.info(metricName, createEmfFormattedMetric(emfMetric));
    }

    public publishMetricWithDimensions(
        metricName: MetricName | ToolbarMetricName,
        dimensions: DimensionSet,
        value = 1
    ): void {
        const emfMetric: EmfMetric = {
            name: metricName,
            value: value,
            unit: "Count",
            dimensions,
        };
        void this.info(metricName, createEmfFormattedMetric(emfMetric));
    }

    public publishNumericMetric(
        metricName: MetricName | ToolbarMetricName,
        value: number,
        dimensions?: DimensionSet
    ): void {
        const emfMetric: EmfMetric = {
            name: metricName,
            value: value,
            unit: "None",
            dimensions: dimensions ?? {},
        };
        void this.info(metricName, createEmfFormattedMetric(emfMetric));
    }

    /**
     * Publishes metrics following Coral's naming convention where:
     * 2xx -> Success
     * 4xx -> Error
     * 5xx -> Fault
     *
     * @param metricName - The operation/API name
     * @param httpStatus - HTTP status code
     */
    public publishHttpStatusMetric(
        metricName: MetricName,
        httpStatus: number
    ): void {
        let outcome: MetricOutcome;

        if (httpStatus >= 200 && httpStatus < 300) {
            outcome = MetricOutcome.SUCCESS;
        } else if (httpStatus >= 400 && httpStatus < 500) {
            outcome = MetricOutcome.ERROR;
        } else if (httpStatus >= 500 && httpStatus < 600) {
            outcome = MetricOutcome.FAULT;
        } else {
            console.warn(
                `An unexpected httpStatusCode value was returned from ${metricName}`
            );
            return;
        }

        const emfMetric: EmfHttpStatusCodeMetric = {
            name: outcome,
            value: 1,
            unit: "Count",
            httpStatusCode: httpStatus,
            dimensions: {
                Operation: metricName,
            },
        };
        void this.info(metricName, createEmfHttpStatusCodeMetric(emfMetric));
    }
}

// Katal uses SendBeacon API to send messages
// Max message size for SendBeacon is determined by userAgent being used. For Chrome max size is 65536
// https://stackoverflow.com/questions/28989640/navigator-sendbeacon-data-size-limits
const BEACON_LOG_CHAR_SIZE = 65536;
const LOG_MAX_SAFETY_PADDING = 0.9;

let logger: ErmineLogger;

/**
 * TODO [LOWA-30534] from: https://tiny.amazon.com/1k3b7e09n
 *
 * Returns the singleton ErmineLogger instance.
 * @param forceReset - If true, creates a new logger instance instead of returning the cached one.
 *                     Should only be used in tests to ensure test isolation.
 */
export const getLogger = (forceReset = false): ErmineLogger => {
    if (!forceReset && logger) {
        return logger;
    }

    const environmentConfigElement = document.getElementById(
        ConfigElements.ENV_INFO
    );

    if (!environmentConfigElement) {
        console.warn(
            "env-info element not found, logging may not work properly"
        );
        return new ErmineLogger();
    }

    const environmentConfig = JSON.parse(environmentConfigElement.innerHTML);
    const { stage, loggerEndpoint = null } = environmentConfig;

    logger = new ErmineLogger({
        url: loggerEndpoint,
        logToConsole: stage !== "prod",
        // https://sage.amazon.dev/posts/1371557?t=7
        maxLogLineSize: Math.floor(
            BEACON_LOG_CHAR_SIZE * LOG_MAX_SAFETY_PADDING
        ),
    });
    logger.addErrorListener();

    return logger;
};
